// src/app/modules/reportsGeneracionDocumentos/ReportList.tsx
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import { useIntl } from 'react-intl'
import { getTokenConfig } from '../../../../src/auth'
import { IReport, ICustomer } from './core/Report.Model'
import moment from 'moment'
import Pagination from './Pagination'
import {
  fetchCustomers,
  fetchExcelReport,
  fetchRequests,
} from './apis/requestsApi'
import ReportModal from './ReportModal'
import Swal, { SweetAlertOptions } from 'sweetalert2'

const ReportList: React.FC = () => {
  const intl = useIntl()

  // --- State hooks ---
  const [data, setData] = useState<IReport[]>([])
  const [customers, setCustomer] = useState<ICustomer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalPages, setTotalPages] = useState(0)
  const [timezone, setTimezone] = useState('')

  const [modalVisible, setModalVisible] = useState(false)
  const [selectedReport, setSelectedReport] = useState<IReport | null>(null)
  const [firstClick, setFirstClick] = useState(true)

  const [searchValue, setSearchValue] = useState('')
  const [searchCustomer, setSearchCustomer] = useState('')
  const [createdAtDateInit, setCreatedAtDateInit] = useState('')
  const [createdAtDateEnd, setCreatedAtDateEnd] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')

  const [orderType, setOrderType] = useState<string>('status,name,asc')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc')

  // --- Hook calls must be unconditional: memoize data here ---
  const memoizedData = useMemo(() => data || [], [data])

  // --- Effect: open modal on first data load ---
  useEffect(() => {
    if (firstClick && memoizedData.length > 0) {
      setSelectedReport(memoizedData[0])
      setModalVisible(true)
      setFirstClick(false)
    }
  }, [memoizedData, firstClick])

  const handleCloseModal = useCallback(() => {
    setSelectedReport(null)
    setModalVisible(false)
  }, [])

  // --- Reset filters ---
  const handleReset = async () => {
    setCurrentPage(1)
    setSearchValue('')
    setSearchCustomer('')
    setCreatedAtDateInit('')
    setCreatedAtDateEnd('')
    setSelectedProduct('')
  }

  // --- Trigger search on filter clear ---
  useEffect(() => {
    if (
      searchValue === '' &&
      searchCustomer === '' &&
      createdAtDateInit === '' &&
      createdAtDateEnd === '' &&
      selectedProduct === ''
    ) {
      handleSearch()
    }
  }, [
    searchValue,
    searchCustomer,
    createdAtDateInit,
    createdAtDateEnd,
    selectedProduct,
  ])

  // --- Change order type/direction ---
  const handleOrderChange = (newOrder: string) => {
    setOrderType(newOrder)
    setOrderDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'))
  }

  // --- Download CSV ---
  const handleDownload = async () => {
    try {
      Swal.fire({
        html: `<div class="spinner-container" style="overflow:hidden;">
                 <i class="fas fa-spinner fa-spin"></i>
                 <span>Generando reporte...</span>
               </div>`,
        showConfirmButton: false,
        allowOutsideClick: false,
      } as SweetAlertOptions)

      const config = await getTokenConfig()
      const url = await fetchExcelReport(
        config,
        searchValue,
        createdAtDateInit,
        createdAtDateEnd,
        orderType,
        orderDirection,
        searchCustomer,
        selectedProduct
      )
      if (url) {
        const link = document.createElement('a')
        link.href = url
        link.download = 'customer_validateidentity_report.csv'
        link.click()
      } else {
        console.error('URL de reporte no disponible.')
      }
      Swal.close()
    } catch (err) {
      Swal.close()
      console.error(err)
    }
  }

  // --- Fetch customers list ---
  useEffect(() => {
    ;(async () => {
      try {
        const config = await getTokenConfig()
        const { customers } = await fetchCustomers(config)
        setCustomer(Array.isArray(customers) ? customers : [])
      } catch (err: any) {
        setError(err)
      }
    })()
  }, [])

  // --- Fetch paginated requests ---
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const config = await getTokenConfig()
        const resp = await fetchRequests(
          config,
          limit,
          currentPage,
          searchValue,
          createdAtDateInit,
          createdAtDateEnd,
          orderType,
          orderDirection,
          searchCustomer,
          selectedProduct
        )
        setData(Array.isArray(resp.requests) ? resp.requests : [])
        setTotalPages(resp.totalPages ?? 0)
        setTimezone(resp.timezone ?? '')
      } catch (err: any) {
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [
    currentPage,
    limit,
    orderType,
    orderDirection,
    searchValue,
    searchCustomer,
    createdAtDateInit,
    createdAtDateEnd,
    selectedProduct,
  ])

  // --- Search handler ---
  const handleSearch = async () => {
    setCurrentPage(1)
    await new Promise<void>((resolve) => {
      resolve()
    })
    const config = await getTokenConfig()
    const resp = await fetchRequests(
      config,
      limit,
      currentPage,
      searchValue,
      createdAtDateInit,
      createdAtDateEnd,
      orderType,
      orderDirection,
      searchCustomer,
      selectedProduct
    )
    setData(Array.isArray(resp.requests) ? resp.requests : [])
    setTotalPages(resp.totalPages ?? 0)
    setTimezone(resp.timezone ?? '')
  }

  // --- Early returns for loading / error ---
  if (isLoading) {
    return <div>{intl.formatMessage({ id: 'PLEASE.WAIT' })}</div>
  }
  if (error) {
    return <div>Ocurrió un error</div>
  }

  // --- Main render ---
  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-body py-3'>
        {/* Filters row */}
        <div className='row flex-wrap mb-5'>
          <div className='col-lg-3 col-md-6 col-sm-12 mb-3'>
            <div className='form-group form-label-group'>
              <label className='form-label'>Buscar</label>
              <input
                type='text'
                className='form-control form-control-solid form-control-lg'
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </div>
          <div className='col-lg-3 col-md-6 col-sm-12 mb-3'>
            <div className='form-group form-label-group'>
              <label className='form-label'>{intl.formatMessage({ id: 'REPORT.PARTNER.C' })}</label>
              <select
                value={searchCustomer}
                onChange={(e) => setSearchCustomer(e.target.value)}
                className='form-select form-select-solid form-control-lg'
              >
                <option value=''>Selecciona un socio comercial</option>
                {customers.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className='col-lg-3 col-md-6 col-sm-12 mb-3'>
            <div className='form-group form-label-group'>
              <label className='form-label'>Fecha de inicio</label>
              <input
                type='date'
                className='form-control form-control-solid form-control-lg'
                value={createdAtDateInit}
                onChange={(e) => setCreatedAtDateInit(e.target.value)}
              />
            </div>
          </div>
          <div className='col-lg-3 col-md-6 col-sm-12 mb-3'>
            <div className='form-group form-label-group'>
              <label className='form-label'>Fecha fin</label>
              <input
                type='date'
                className='form-control form-control-solid form-control-lg'
                value={createdAtDateEnd}
                onChange={(e) => setCreatedAtDateEnd(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* Buttons */}
        <div className='row flex-wrap mb-5'>
          <div className='col-12 d-flex justify-content-end'>
            <button onClick={handleSearch} className='btn btn-primary rounded-pill me-2'>
              {intl.formatMessage({ id: 'SEARCH' })}
            </button>
            <button onClick={handleReset} className='btn btn-secondary rounded-pill me-2'>
              {intl.formatMessage({ id: 'RESET' })}
            </button>
            <button onClick={handleDownload} className='btn btn-secondary rounded-pill'>
              {intl.formatMessage({ id: 'DOWNLOAD' })}
            </button>
          </div>
        </div>
        {/* Table */}
        <div className='table-responsive mb-20'>
          {memoizedData.length === 0 ? (
            <p className='mb-0'>
              <strong>No hay resultados disponibles</strong>
            </p>
          ) : (
            <table className='table table-striped' id='table'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200'>
                  <th onClick={() => handleOrderChange('id,asc')} className='min-w-50px'>#</th>
                  <th onClick={() => handleOrderChange('imei,asc')} className='min-w-50px'>Template</th>
                  <th onClick={() => handleOrderChange('customerName,asc')} className='min-w-50px'>
                    Socio comercial
                  </th>
                  <th>Producto</th>
                  <th onClick={() => handleOrderChange('createdAt,asc')} className='min-w-150px'>
                    {intl.formatMessage({ id: 'IDENTITY_VALIDATION.DATE' })}
                  </th>
                  <th onClick={() => handleOrderChange('statusCode,asc')} className='min-w-150px'>
                    {intl.formatMessage({ id: 'IDENTITY_VALIDATION.STATUS' })}
                  </th>
                  <th onClick={() => handleOrderChange('email,asc')} className='min-w-150px'>
                    Email del solicitante
                  </th>
                  <th onClick={() => handleOrderChange('userName,asc')} className='min-w-150px'>
                    {intl.formatMessage({ id: 'IDENTITY_VALIDATION_CUSTOMER_USERNAME' })}
                  </th>
                  <th className='min-w-150px'>
                    {intl.formatMessage({ id: 'IDENTITY_VALIDATION.ACTIONS' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {memoizedData.map((item, idx) => {
                  const count = (currentPage - 1) * limit + idx + 1
                  return (
                    <tr key={item.id}>
                      <td style={{ width: '14ch' }}>{count}</td>
                      <td style={{ width: '14ch' }}>{item.entityId}</td>
                      <td style={{ width: '14ch' }}>{item.customerName}</td>
                      <td style={{ width: '14ch' }}>{item.subproductName}</td>
                      <td style={{ width: '14ch' }}>
                        {moment(item.createdAt).tz(timezone).format('DD-MM-YYYY')}
                      </td>
                      <td style={{ width: '14ch' }}>
                        {item.statusCode === '200' ? 'Aceptado' : 'Rechazado'}
                      </td>
                      <td style={{ width: '14ch' }}>{item.email}</td>
                      <td style={{ width: '14ch' }}>{item.userName}</td>
                      <td style={{ width: '14ch' }}>
                        {item.downloadUrl ? (
                          <a href={item.downloadUrl} download className='btn btn-primary btn-icon btn-sm rounded-pill'>
                            <i className='bi bi-cloud-download'></i>
                          </a>
                        ) : (
                          <span>Descarga no disponible</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          {selectedReport && (
            <ReportModal report={selectedReport} visible={modalVisible} onClose={handleCloseModal} />
          )}
        </div>
        {/* Pagination */}
        {memoizedData.length > 0 && (
          <div className='d-flex justify-content mb-4 same-col'>
            <div>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className='form-select form-select-sm custom-select-page'
              >
                {[10, 15, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                limit={limit}
                lengthData={memoizedData.length}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export { ReportList }
